import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  Save,
  X,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';

const MarketShareMatrix = ({ onClose, onSaveSuccess }) => {
  const queryClient = useQueryClient();
  const [editedCells, setEditedCells] = useState({});
  const [showApplyTemplateDialog, setShowApplyTemplateDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch matrix data
  const { data: matrixData, isLoading } = useQuery({
    queryKey: ['marketShareMatrix'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/market-share-matrix?year=2025');
      return response.data;
    }
  });

  // Bulk upsert mutation
  const bulkUpsertMutation = useMutation({
    mutationFn: async (entries) => {
      const response = await apiClient.post('/api/competitive-intelligence/segment-rules/bulk-upsert', {
        entries,
        confidence_level: 'medium'
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['marketShareMatrix']);
      queryClient.invalidateQueries(['marketShareByPartType']);
      queryClient.invalidateQueries(['marketShareByPartTypeV2']);
      queryClient.invalidateQueries(['marketShareSummary']);
      setEditedCells({});
      setHasUnsavedChanges(false);
      if (onSaveSuccess) onSaveSuccess(data);
    }
  });

  // Apply templates mutation
  const applyTemplatesMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/api/competitive-intelligence/segment-rules/apply-templates', {});
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['marketShareMatrix']);
      setShowApplyTemplateDialog(false);
    }
  });

  const competitors = matrixData?.competitors || [];
  const partTypes = matrixData?.part_types || [];
  const cells = matrixData?.cells || {};
  const generalRules = matrixData?.general_rules || [];
  const configStatus = matrixData?.configuration_status || {};

  // Calculate column totals
  const columnTotals = useMemo(() => {
    const totals = {};
    partTypes.forEach(pt => {
      let total = 0;
      competitors.forEach(comp => {
        const key = `${comp.id}|${pt.id}`;
        const editedValue = editedCells[key];
        const originalValue = cells[key]?.share_percent;
        const value = editedValue !== undefined ? editedValue : originalValue;
        if (value) total += parseFloat(value) || 0;
      });
      totals[pt.id] = total;
    });
    return totals;
  }, [competitors, partTypes, cells, editedCells]);

  // Handle cell edit
  const handleCellChange = (competitorId, partTypeId, value) => {
    const key = `${competitorId}|${partTypeId}`;
    const numValue = value === '' ? null : parseFloat(value);
    
    // Check if value is same as original
    const originalValue = cells[key]?.share_percent;
    if (numValue === originalValue) {
      // Remove from edited cells if same as original
      const newEdited = { ...editedCells };
      delete newEdited[key];
      setEditedCells(newEdited);
      setHasUnsavedChanges(Object.keys(newEdited).length > 0);
    } else {
      setEditedCells(prev => ({ ...prev, [key]: numValue }));
      setHasUnsavedChanges(true);
    }
  };

  // Get cell value (edited or original)
  const getCellValue = (competitorId, partTypeId) => {
    const key = `${competitorId}|${partTypeId}`;
    if (editedCells[key] !== undefined) {
      return editedCells[key] === null ? '' : editedCells[key];
    }
    return cells[key]?.share_percent ?? '';
  };

  // Check if cell was edited
  const isCellEdited = (competitorId, partTypeId) => {
    const key = `${competitorId}|${partTypeId}`;
    return editedCells[key] !== undefined;
  };

  // Save all changes
  const handleSave = () => {
    const entries = Object.entries(editedCells).map(([key, value]) => {
      const [competitorId, partTypeId] = key.split('|');
      return {
        competitor_id: competitorId,
        part_type_id: partTypeId,
        share_percent: value
      };
    });
    
    if (entries.length > 0) {
      bulkUpsertMutation.mutate(entries);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setEditedCells({});
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading matrix data...</div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Market Share Matrix</h3>
            <p className="text-sm text-slate-500">
              Enter market share percentages for each competitor and part type
            </p>
          </div>
          <div className="flex items-center gap-2">
            {generalRules.length > 0 && configStatus.unconfigured_part_types > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowApplyTemplateDialog(true)}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Apply Templates ({generalRules.length})
              </Button>
            )}
            {hasUnsavedChanges && (
              <>
                <Button variant="outline" size="sm" onClick={handleDiscard}>
                  <X className="h-4 w-4 mr-2" />
                  Discard
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  disabled={bulkUpsertMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {bulkUpsertMutation.isPending ? 'Saving...' : `Save Changes (${Object.keys(editedCells).length})`}
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Configuration Status */}
        <div className="flex items-center gap-4 text-sm">
          <Badge variant={configStatus.configured === configStatus.total ? "default" : "secondary"}>
            {configStatus.configured} / {configStatus.total} Part Types Configured
          </Badge>
          <span className="text-slate-500">
            {configStatus.filled_cells} / {configStatus.total_cells} cells filled
          </span>
        </div>

        {/* Matrix Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left p-3 font-medium text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[180px]">
                    Competitor
                  </th>
                  {partTypes.map(pt => (
                    <th key={pt.id} className="text-center p-3 font-medium text-slate-700 min-w-[120px]">
                      <div className="flex flex-col items-center gap-1">
                        <span>{pt.name}</span>
                        <Badge variant="outline" className="text-xs font-normal">
                          {pt.code}
                        </Badge>
                        {pt.is_configured ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitors.map((comp, compIdx) => (
                  <tr 
                    key={comp.id} 
                    className={`border-b hover:bg-slate-50/50 ${comp.is_my_company ? 'bg-blue-50/30' : ''}`}
                  >
                    <td className={`p-3 sticky left-0 z-10 ${comp.is_my_company ? 'bg-blue-50/50' : 'bg-white'}`}>
                      <div className="flex items-center gap-2">
                        {comp.is_my_company && (
                          <Badge className="bg-blue-600 text-white text-xs">You</Badge>
                        )}
                        <span className={comp.is_my_company ? 'font-medium text-blue-900' : 'text-slate-700'}>
                          {comp.name}
                        </span>
                        {comp.short_name && (
                          <span className="text-slate-400 text-xs">({comp.short_name})</span>
                        )}
                      </div>
                    </td>
                    {partTypes.map(pt => {
                      const value = getCellValue(comp.id, pt.id);
                      const isEdited = isCellEdited(comp.id, pt.id);
                      const key = `${comp.id}|${pt.id}`;
                      const hasOriginalRule = cells[key]?.has_rule;
                      
                      return (
                        <td key={pt.id} className="p-2 text-center">
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={value}
                              onChange={(e) => handleCellChange(comp.id, pt.id, e.target.value)}
                              className={`w-20 mx-auto text-center h-9 ${
                                isEdited 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : hasOriginalRule 
                                    ? 'border-green-200 bg-green-50/30' 
                                    : ''
                              }`}
                              placeholder="-"
                            />
                            {isEdited && (
                              <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                
                {/* Totals Row */}
                <tr className="bg-slate-100 font-medium border-t-2">
                  <td className="p-3 sticky left-0 bg-slate-100 z-10">
                    <span className="text-slate-700">TOTAL</span>
                  </td>
                  {partTypes.map(pt => {
                    const total = columnTotals[pt.id] || 0;
                    const isOver100 = total > 100;
                    const isComplete = total >= 90 && total <= 100;
                    
                    return (
                      <td key={pt.id} className="p-3 text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className={`text-lg ${
                              isOver100 
                                ? 'text-amber-600' 
                                : isComplete 
                                  ? 'text-green-600' 
                                  : 'text-slate-600'
                            }`}>
                              {total.toFixed(1)}%
                              {isOver100 && <AlertTriangle className="inline h-4 w-4 ml-1" />}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isOver100 ? (
                              <p className="text-amber-200">Total exceeds 100% - this may indicate overlapping estimates</p>
                            ) : isComplete ? (
                              <p className="text-green-200">Good coverage!</p>
                            ) : (
                              <p>{(100 - total).toFixed(1)}% unassigned</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
                
                {/* Available Row */}
                <tr className="bg-amber-50/50">
                  <td className="p-3 sticky left-0 bg-amber-50/50 z-10">
                    <span className="text-amber-700">Available</span>
                  </td>
                  {partTypes.map(pt => {
                    const total = columnTotals[pt.id] || 0;
                    const available = Math.max(0, 100 - total);
                    
                    return (
                      <td key={pt.id} className="p-3 text-center">
                        <span className={`${available > 50 ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                          {available.toFixed(1)}%
                        </span>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex items-center gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border border-green-200 bg-green-50/30" />
            <span>Has existing rule</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border border-blue-500 bg-blue-50" />
            <span>Edited (unsaved)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span>Part type configured</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span>Needs configuration</span>
          </div>
        </div>

        {/* Apply Templates Dialog */}
        <Dialog open={showApplyTemplateDialog} onOpenChange={setShowApplyTemplateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Apply Template Rules
              </DialogTitle>
              <DialogDescription>
                You have {generalRules.length} general rule(s) that can be applied to all part types.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>What are template rules?</AlertTitle>
                <AlertDescription className="text-sm">
                  Template rules are market share assignments that were not linked to a specific part type. 
                  Applying them will create part-type-specific rules for all unconfigured part types.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Templates to apply:</p>
                {generalRules.map(rule => (
                  <div key={rule.rule_id} className="text-sm bg-slate-50 p-2 rounded flex justify-between">
                    <span>{rule.competitor_name}</span>
                    <span className="font-medium">{rule.share_percent}%</span>
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-slate-500">
                This will create rules for {configStatus.unconfigured_part_types} unconfigured part type(s).
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyTemplateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => applyTemplatesMutation.mutate()}
                disabled={applyTemplatesMutation.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {applyTemplatesMutation.isPending ? 'Applying...' : 'Apply Templates'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default MarketShareMatrix;
